import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const POLL_MS = 30_000;

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const timer = useRef(null);

  // Poll unread count whenever the user is logged in.
  useEffect(() => {
    if (!user) {
      setUnread(0);
      setItems([]);
      return;
    }
    const tick = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        setUnread(data?.count || 0);
      } catch {
        // soft-fail — silent
      }
    };
    tick();
    timer.current = setInterval(tick, POLL_MS);
    return () => clearInterval(timer.current);
  }, [user]);

  // Load the full list on dropdown open.
  const handleOpenChange = async (next) => {
    setOpen(next);
    if (next && user) {
      try {
        const { data } = await api.get("/notifications");
        setItems(Array.isArray(data) ? data : []);
      } catch {
        setItems([]);
      }
    }
  };

  const handleItemClick = async (n) => {
    setOpen(false);
    try {
      if (!n.read) {
        await api.post(`/notifications/${n.notification_id}/read`);
        setUnread((u) => Math.max(0, u - 1));
      }
    } catch {
      // ignore
    }
    if (n.link) navigate(n.link);
  };

  const handleMarkAll = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      // ignore
    }
  };

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-full hover:bg-stone-100"
          data-testid="notifications-bell"
        >
          <Bell className="h-5 w-5 text-stone-700" />
          {unread > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C05640] text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notifications-unread-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[360px] max-w-[92vw] p-0 overflow-hidden"
        data-testid="notifications-dropdown"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <div className="font-display text-base text-stone-900">Notifications</div>
          {items.some((n) => !n.read) && (
            <button
              onClick={handleMarkAll}
              className="text-[11px] uppercase tracking-wide font-bold text-stone-500 hover:text-stone-800"
              data-testid="notifications-mark-all-read"
            >
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-stone-500" data-testid="notifications-empty">
              No notifications yet
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.notification_id}
                onClick={() => handleItemClick(n)}
                className={`w-full text-left px-4 py-3 border-b border-stone-100 hover:bg-stone-50 flex gap-3 ${
                  n.read ? "" : "bg-amber-50/40"
                }`}
                data-testid={`notification-item-${n.notification_id}`}
              >
                <span
                  className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${
                    n.read ? "bg-stone-200" : "bg-[#C05640]"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-stone-900 truncate">{n.title}</div>
                  {n.body && <div className="text-xs text-stone-600 mt-0.5">{n.body}</div>}
                  <div className="text-[10px] uppercase tracking-wide text-stone-400 mt-1">
                    {timeAgo(n.created_at)}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
